import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Message } from 'semantic-ui-react';
import { EmployeeRecordValue, EmployeeRecordValueCreateUpdate, EmployeeRecordValueCreateUpdateTypeOfValueEnum } from 'store/generated/admin/employee-record-values';
import { employeeRecordApi } from '../api/employeeRecordValueApi';

interface Props {
    open: boolean;
    organizationId: number;
    onClose: () => void;
    onSuccess: () => void;
    fieldToEdit?: EmployeeRecordValue | null;
}

const OrganizationEmployeeRecordModal: React.FC<Props> = ({ open, organizationId, onClose, onSuccess, fieldToEdit }) => {
    const [name, setName] = useState('');
    const [typeOfValue, setTypeOfValue] = useState(1);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [createFieldType, { isLoading: isCreating }] = employeeRecordApi.endpoints.apiAdminV1EmployeeRecordValuesCreate.useMutation();
    const [updateFieldType, { isLoading: isUpdating }] = employeeRecordApi.endpoints.apiAdminV1EmployeeRecordValuesUpdate.useMutation();

    useEffect(() => {
        if (fieldToEdit) {
            setName(fieldToEdit.name || '');
            setTypeOfValue(fieldToEdit.type_of_value || 1);
        } else {
            setName('');
            setTypeOfValue(1);
        }
        setErrors({});
    }, [fieldToEdit, open]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!name.trim()) newErrors.name = 'Field name is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        const payload: EmployeeRecordValueCreateUpdate = {
            name,
            organization: organizationId,
            type_of_value: typeOfValue as EmployeeRecordValueCreateUpdateTypeOfValueEnum,
        };

        try {
            if (fieldToEdit?.id) {
                await updateFieldType({ id: fieldToEdit.id, employeeRecordValueCreateUpdate: payload }).unwrap();
            } else {
                await createFieldType({ employeeRecordValueCreateUpdate: payload }).unwrap();
            }
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Save failed:', err);
        }
    };

    const typeOfValueOptions = [
        // TP: This is an IL vestige. Number supports integers and decimals. No support for Date and Text currently.
        // { key: 1, text: 'Text/Character', value: 1 },
        { key: 2, text: 'Number', value: 2 },
        //{ key: 3, text: 'Decimal', value: 3 },
        //{ key: 4, text: 'Date', value: 4 },
    ];

    return (
        <Modal open={open} onClose={onClose} size="small">
            <Modal.Header>{fieldToEdit ? 'Edit Member Record' : 'Create Member Record'}</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field error={!!errors.name} required>
                        <label>Field Name</label>
                        <Form.Input value={name} onChange={(e, { value }) => setName(value)} />
                        {errors.name && <Message negative size="tiny">{errors.name}</Message>}
                    </Form.Field>
                    <Form.Field>
                        <label>Type of Value</label>
                        <Form.Select
                            options={typeOfValueOptions}
                            value={typeOfValue}
                            onChange={(e, { value }) => setTypeOfValue(value as number)}
                            fluid
                        />
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} disabled={isCreating || isUpdating}>Cancel</Button>
                <Button
                    primary
                    onClick={handleSave}
                    loading={isCreating || isUpdating}
                    disabled={!name}
                >
                    Save
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default OrganizationEmployeeRecordModal;
